import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles, ArrowRight, CheckCircle2, ShieldCheck, CreditCard, Clock,
    Crown, Star, Compass, Lock, Calendar,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { getPageStrings } from "@/lib/pageStrings";
import LanguagePicker from "@/components/LanguagePicker";

const TIER_ICONS = {
    explorer: Compass,
    investor: Star,
    developer: Crown,
};

const TIER_ACCENTS = {
    explorer: "text-fiji-green border-fiji-green/30",
    investor: "text-coral border-coral/40 ring-2 ring-coral/30",
    developer: "text-ocean dark:text-turquoise border-ocean/30 dark:border-turquoise/30",
};

export default function Concierge() {
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [purchaseTier, setPurchaseTier] = useState(null);
    const { lang } = useLang();
    const t = getPageStrings("concierge", lang);

    useEffect(() => {
        api.get("/concierge/tiers")
            .then(({ data }) => setTiers(data.tiers || []))
            .catch(() => toast.error("Could not load tiers"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="concierge-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-20 lg:py-28">
                <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-coral/10 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-turquoise/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                        <Sparkles className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            {t.heroKicker}
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5">
                        {t.heroTitlePre}<span className="italic text-turquoise">{t.heroTitleAccent}</span>
                        <br />{t.heroTitlePost}
                    </h1>
                    <p className="font-app text-base sm:text-lg text-white/85 leading-relaxed max-w-3xl mx-auto">
                        {t.heroSubtitle}
                    </p>
                    <div className="mt-6 flex justify-center">
                        <LanguagePicker variant="dark" testId="concierge-lang-select" />
                    </div>
                </div>
            </section>

            {/* TIERS GRID */}
            <section className="py-16 lg:py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="text-center font-app text-muted-foreground py-20">{t.tiersLoading}</div>
                    ) : (
                        <div className="grid lg:grid-cols-3 gap-6">
                            {tiers.map((tier) => {
                                const Icon = TIER_ICONS[tier.id] || Star;
                                return (
                                    <Card
                                        key={tier.id}
                                        data-testid={`concierge-tier-${tier.id}`}
                                        className={`p-7 bg-card relative ${TIER_ACCENTS[tier.id]} hover:shadow-xl transition-all flex flex-col`}
                                    >
                                        {tier.popular && (
                                            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral hover:bg-coral text-white font-app uppercase tracking-wider text-[10px] px-3 py-1">
                                                {t.tierMostPopular}
                                            </Badge>
                                        )}
                                        <div className={`h-12 w-12 rounded-lg grid place-items-center mb-4 ${
                                            tier.id === "explorer" ? "bg-fiji-green/15" :
                                            tier.id === "investor" ? "bg-coral/15" :
                                            "bg-ocean/15 dark:bg-turquoise/15"
                                        }`}>
                                            <Icon className={`h-6 w-6 ${
                                                tier.id === "explorer" ? "text-fiji-green" :
                                                tier.id === "investor" ? "text-coral" :
                                                "text-ocean dark:text-turquoise"
                                            }`} />
                                        </div>

                                        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-app font-bold mb-1">
                                            {tier.tagline}
                                        </div>
                                        <h2 className="font-display text-3xl font-bold text-foreground leading-tight">
                                            {tier.label}
                                        </h2>
                                        <div className="flex items-baseline gap-2 mt-3 mb-2">
                                            <span className="font-display text-4xl font-bold text-foreground tabular-nums">
                                                {tier.price_label}
                                            </span>
                                            <span className="text-sm text-muted-foreground font-app">USD</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-app mb-5">
                                            <Clock className="h-3 w-3" />
                                            {tier.duration}
                                        </div>

                                        <p className="font-app text-sm text-foreground font-semibold mb-5 leading-snug">
                                            {tier.headline}
                                        </p>

                                        <ul className="space-y-2 mb-6 flex-grow">
                                            {tier.includes.map((inc, j) => (
                                                <li key={j} className="flex items-start gap-2 text-sm font-app text-foreground/85">
                                                    <CheckCircle2 className="h-4 w-4 text-fiji-green mt-0.5 shrink-0" />
                                                    <span className={inc.startsWith("Everything") ? "font-semibold text-foreground" : ""}>
                                                        {inc}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="bg-muted/40 dark:bg-card/60 border border-border rounded-lg p-3 mb-5">
                                            <p className="font-app text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
                                                {t.tierOutcomesLabel}
                                            </p>
                                            <ul className="space-y-1">
                                                {tier.outcomes.map((o, j) => (
                                                    <li key={j} className="text-xs font-app text-foreground/80 leading-snug">
                                                        → {o}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <Button
                                            data-testid={`concierge-buy-${tier.id}`}
                                            onClick={() => setPurchaseTier(tier)}
                                            className={`w-full font-app text-white ${
                                                tier.popular
                                                    ? "bg-coral hover:bg-coral-deep"
                                                    : "bg-ocean-green hover:bg-ocean"
                                            }`}
                                        >
                                            <CreditCard className="h-4 w-4 mr-2" />
                                            {t.tierBuyPrefix} {tier.label} — {tier.price_label}
                                        </Button>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* TRUST STRIP */}
            <section className="py-12 bg-secondary/40 dark:bg-card/40 border-y border-border">
                <div className="max-w-5xl mx-auto px-4 grid sm:grid-cols-3 gap-6">
                    {[
                        { icon: ShieldCheck, ...t.trustStripe },
                        { icon: Lock, ...t.trustRefund },
                        { icon: Calendar, ...t.trustKickoff },
                    ].map((b) => (
                        <div key={b.title} className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-lg bg-coral/10 grid place-items-center shrink-0">
                                <b.icon className="h-5 w-5 text-coral" />
                            </div>
                            <div>
                                <div className="font-display text-base font-bold text-foreground mb-1">{b.title}</div>
                                <p className="font-app text-xs text-muted-foreground leading-relaxed">{b.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ-LITE */}
            <section className="py-16 lg:py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="font-display text-2xl lg:text-3xl font-bold mb-8 text-foreground text-center">
                        {t.faqTitle}
                    </h2>
                    <div className="space-y-5">
                        {t.faq.map((f) => (
                            <Card key={f.q} className="p-5 bg-card">
                                <div className="font-display text-base font-bold text-foreground mb-1.5">{f.q}</div>
                                <p className="font-app text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                            </Card>
                        ))}
                    </div>
                    <div className="mt-10 text-center">
                        <p className="font-app text-sm text-muted-foreground mb-3">{t.quizPrompt}</p>
                        <Button asChild variant="outline" data-testid="concierge-quiz-cta">
                            <Link to="/invest">{t.quizCta} <ArrowRight className="h-4 w-4 ml-2" /></Link>
                        </Button>
                    </div>
                </div>
            </section>

            {purchaseTier && (
                <CheckoutDialog
                    tier={purchaseTier}
                    onClose={() => setPurchaseTier(null)}
                />
            )}
        </div>
    );
}

function CheckoutDialog({ tier, onClose }) {
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", country: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email) {
            toast.error("Name and email are required");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/concierge/checkout", {
                tier_id: tier.id,
                full_name: form.full_name,
                email: form.email,
                phone: form.phone,
                country: form.country,
                notes: form.notes,
                origin_url: window.location.origin,
            });
            if (data.url) {
                window.location.href = data.url;  // redirect to Stripe Checkout
            } else {
                toast.error("Could not start checkout");
                setSubmitting(false);
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Checkout could not be started");
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="concierge-checkout-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">
                        Purchase: {tier.label} Tier — {tier.price_label} USD
                    </DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        We'll redirect you to Stripe to complete payment. Cards only. {tier.duration}.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-3" data-testid="concierge-checkout-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Full name *</Label>
                            <Input data-testid="concierge-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required className="bg-card text-foreground" />
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Email *</Label>
                            <Input data-testid="concierge-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="bg-card text-foreground" />
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Phone (E.164)</Label>
                            <Input data-testid="concierge-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1..." className="bg-card text-foreground" />
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Country</Label>
                            <Input data-testid="concierge-country" value={form.country} onChange={(e) => update("country", e.target.value)} placeholder="USA / Australia / NZ…" className="bg-card text-foreground" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Anything we should know? (optional)</Label>
                        <Textarea data-testid="concierge-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} placeholder="Investment thesis, timeline, location preferences…" className="bg-card text-foreground resize-none" />
                    </div>

                    <div className="bg-secondary/50 dark:bg-card/50 border border-border rounded-lg p-3 text-xs font-app text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5 inline mr-1.5 text-fiji-green" />
                        Secure card payment via Stripe. 7-day refund. Onboarding email arrives within 1 business day.
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="concierge-pay-btn" className="bg-coral hover:bg-coral-deep text-white">
                            <CreditCard className="h-4 w-4 mr-2" />
                            {submitting ? "Redirecting…" : `Pay ${tier.price_label} & continue`}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
